// AddAppCatalogCard — one ready-made workspace app on the /add-app catalog
// rail (WORKSPACE_APPS.md "Discovery"). Rendered from root._wsAppsCatalog rows
// via childrenAs:'state' — child state carries { id, owner, projectKey, name,
// category, description } (project key rides as `projectKey`; `key` is the
// diff key). Install is shown to org admins only — the server enforces
// workspace owner/admin on the settings write; the shell just doesn't offer
// the button to everyone else. "Details" deep-links the marketplace item.

// Fable 2026-08-09 P1 — the card never flipped to "Active"/hid its Install
// button for a PLURAL (additive) install: loadWorkspaceApps composes
// `root.workspaceProject` via `_composeAiStash(takeover, apps, …)`, whose
// `id` is `(takeover && takeover.id) || null` — for a workspace with no
// single-takeover module (the common case: excalidraw and every other
// /add-app catalog app installs into the ADDITIVE `settings.workspaceApps[]`
// list, never the legacy takeover slot) that `id` is ALWAYS null, so
// `workspaceProject.id === owner/key` could never be true no matter how long
// you waited — not even after a reload. The plural install's real signature
// is membership in `root.workspaceApps` (loadWorkspaceApps, keyed by
// `appId` = lowercased `owner/key`). Check both so a legacy takeover module
// AND a plural additive app both read correctly.
const _isInstalled = (s) => {
  const root = s.root
  if (!root) return false
  const wp = root.workspaceProject
  if (wp && wp.id === `${s.owner}/${s.projectKey}`) return true
  const appId = `${String(s.owner).toLowerCase()}/${String(s.projectKey).toLowerCase()}`
  return (Array.isArray(root.workspaceApps) ? root.workspaceApps : []).some(
    (a) => a && String(a.appId).toLowerCase() === appId
  )
}

export const AddAppCatalogCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'radiusCard',
  minWidth: '0',
  height: '100%',
  boxSizing: 'border-box',
  textAlign: 'left',
  // An opaque card keeps the canvas texture out of the reading surface.
  background: 'var(--color-cardSolid, var(--theme-card-background))',

  TitleRow: {
    flow: 'x',
    gap: 'Z',
    align: 'center space-between',
    flexWrap: 'wrap',
    rowGap: 'Y',
    Name: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      overflowWrap: 'anywhere',
      // THE store-copy resolver (shared/functions/storeItemText.js) — the App
      // Store's own call — so a card says what the store says, in the session
      // language ("კომერცია", where the record's English `name` read
      // "Commerce" in a Georgian session). Nika 2026-10-23: "app names = what
      // a person sees in the workspace App Store". A third party's record
      // resolves no shipped name and keeps its own.
      text: (el, s) => el.call('storeItemText', { key: s.projectKey, name: s.name }, 'name')
    },
    Category: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      textTransform: 'uppercase',
      letterSpacing: '.05em',
      text: (el, s) => s.category
    }
  },

  Description: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    color: 'caption',
    lineHeight: '1.5',
    overflowWrap: 'anywhere',
    flex: '1',
    // Root cause (was rendering the literal "undefined/undefined"): this
    // fallback built an owner/projectKey path WITHOUT checking that either
    // field actually resolved first — any consumer that doesn't populate
    // both (e.g. a bare catalog-sample mount with no child state) got the
    // concatenation of two `undefined`s as visible text. Check the SOURCE
    // fields before building the string (never emit the literal word), and
    // hide the row entirely rather than leave an empty line when a card
    // truly has no description AND no owner/projectKey to fall back to.
    hide: (el, s) => !s.description && !(s.owner && s.projectKey),
    // The app's one line, through THE store-copy resolver
    // (shared/functions/storeItemText.js) — the App Store card's own call —
    // so the card says what the store says, in the session language: the
    // app's App Store listing tagline (the catalogue row with this key
    // carries it), else the record's own `metadata.description`. That record
    // line is English only, and the raw read gave a Georgian session English
    // on every card (lane DESC, gap 2). A record with no listing keeps its own.
    text: (el, s) =>
      el.call('storeItemText', { key: s.projectKey, description: s.description }, 'description') ||
      (s.owner && s.projectKey ? `${s.owner}/${s.projectKey}` : '')
  },

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center flex-start',
    marginTop: 'Z',
    flexWrap: 'wrap',

    // Already the active module on this workspace — house active treatment.
    ActiveChip: {
      hide: (el, s) => !_isInstalled(s),
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'radiusPill',
      background: 'selected',
      color: 'title',
      text: '{{ workspace.catalogCard.active | polyglot }}'
    },

    Button: {
      extends: 'AppCapsuleBtnPrimary',
      hide: (el, s) => {
        const role = s.root?.org?.role
        if (!['owner', 'co-owner', 'admin', 'maintainer'].includes(role)) return true
        return _isInstalled(s)
      },
      // Installs share one pending request. Keep every install control in
      // sync with that lock, and announce activity on the app being added.
      disabled: (el, s) => !!s.root?._wsAppsInstalling,
      ariaBusy: (el, s) =>
        s.root && s.root._wsAppsInstalling === `${s.owner}/${s.projectKey}` ? 'true' : 'false',
      type: 'button',
      flow: 'x',
      align: 'center center',
      gap: 'Y',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      fontSize: 'Z2',
      fontWeight: '700',
      theme: 'primary',
      Label: {
        tag: 'span',
        text: (el, s) =>
          s.root?._wsAppsInstalling === `${s.owner}/${s.projectKey}`
            ? el.call('polyglot', 'workspace.catalogCard.installing')
            : el.call('polyglot', 'workspace.catalogCard.install')
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        if (s.root && s.root._wsAppsInstalling) return
        el.call('installWorkspaceAppRef', { owner: s.owner, key: s.projectKey, name: s.name })
      }
    },

    // The app's App Store page, from the App Store's one builder
    // (marketplace functions/appStoreItemLink.js, registered at boot).
    Link: {
      href: (el, s) => el.call('appStoreItemLink', s.owner, s.projectKey),
      minHeight: 'controlH',
      display: 'inline-flex',
      align: 'center center',
      fontSize: 'Z2',
      color: 'caption',
      ':hover': { color: 'title' },
      ':active': { color: 'title' },
      text: '{{ workspace.catalogCard.details | polyglot }}'
    }
  }
}
